import React, { useState, useContext, useEffect, useRef } from 'react';
import { Table } from "reactstrap";
import { AppContext } from '../application/provider';
import ControlPoiService from "../axios_services/controlpoi.service";
import Loader from "../components/Loader/Loader";
import { ButtonInsert, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalControlPoi from '../components/Modal/ModalControlPoi';
import ArchivoService from '../axios_services/archivos.service';
import { FileMinus } from "lucide-react";

function ControlPoi() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRegistro, setSelectedRegistro] = useState(null);
    const initialFetchDone = useRef(false);

    const [departamento_poi] = useState(state.catalogos.tipos.filter(t => { return t.categoria_id == 39 }));
    const allowedProfiles = [1, 6, 8, 26, 9];
    const canEdit = allowedProfiles.includes(Number(state.user?.perfil_id));

    const isDeptAllowed = (deptId) => {
        const pid = Number(state.user?.perfil_id);
        const dId = Number(deptId);
        if (pid === 8) return true;
        if (pid === 1) return [4, 6, 7, 8, 9].includes(dId);
        if (pid === 26) return [1, 2, 3, 5].includes(dId);
        if (pid === 6 || pid === 9) return [6, 7, 8, 9].includes(dId);
        return false;
    };

    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

    const actividadesPorDepartamento = {
        1: "DOTACION DE PERSONAL",
        2: "RENTABILIDAD MENSUAL POSITIVA",
        3: "POLITICAS Y PROCEDIMIENTOS",
        4: "AUTOMATIZACION DE PROCESOS",
        5: "DOTACION Y COMPRA OPTIMIZADA",
        6: "ALCANCE DE METAS",
        7: "POSICIONAMIENTO DE LA MARCA",
        8: "DOTACION DE BASE DE DATOS",
        9: "ALCANCE DE METAS"
    };

    const lineasUnidad = {
        1: "GERENCIA ADMINISTRACION/SUB GERENCIA DE ADMINISTRACION",
        2: "GERENCIA ADMINISTRACION/SUB GERENCIA DE ADMINISTRACION",
        3: "GERENCIA ADMINISTRACION/SUB GERENCIA DE ADMINISTRACION",
        4: "GERENCIA ADMINISTRACION/SUB GERENCIA DE ADMINISTRACION",
        5: "GERENCIA ADMINISTRACION/SUB GERENCIA DE ADMINISTRACION",
        6: "GERENCIA DE NEGOCIOS/SUB GERENCIA DE NEGOCIOS",
        7: "GERENCIA DE NEGOCIOS/SUB GERENCIA DE NEGOCIOS",
        8: "GERENCIA DE NEGOCIOS/SUB GERENCIA DE NEGOCIOS",
        9: "GERENCIA DE NEGOCIOS/SUB GERENCIA DE NEGOCIOS"
    };

    useEffect(() => {
        if (!initialFetchDone.current) {
            loadInitialPageData();
            initialFetchDone.current = true;
        }
    }, []);

    const loadInitialPageData = async () => {
        setIsLoading(true);
        try {
            const response = await ControlPoiService.getControlPoi();
            setRegistros(response.data || []);
        } catch (error) {
            console.error(error);
            setRegistros([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleNuevoRegistro = () => {
        setSelectedRegistro(null);
        setIsModalOpen(true);
    };

    const handleEditarRegistro = (departamentoId) => {
        setSelectedRegistro({ departamento_id: departamentoId });
        setIsModalOpen(true);
    };

    const handleCloseModal = (shouldReload = false) => {
        setIsModalOpen(false);
        setSelectedRegistro(null);
        if (shouldReload === true) {
            loadInitialPageData();
        }
    };

    const handleSaveRegistro = async (registroData) => {
        setIsLoading(true);
        setIsModalOpen(false);
        try {
            let response;
            if (registroData.control_poi_id) {
                response = await ControlPoiService.updateControlPoi(registroData);
            } else {
                response = await ControlPoiService.insertControlPoi(registroData);
            }
            return response.data || response;
        } catch (error) {
            setIsLoading(false);
            alert(error.response?.data?.message || 'Ocurrió un error al guardar.');
            throw error;
        }
    };

    const handleVerDocumentoPoi = async (control_poi_id) => {
        try {
            const documento = await ArchivoService.getDocumentoPoi(control_poi_id);
            if (documento && documento.url_completa) {
                window.open(documento.url_completa, "_blank", "noopener,noreferrer");
            } else {
                alert("Este registro no tiene un documento adjunto.");
            }
        } catch (error) {
            alert("Ocurrió un error al intentar abrir el documento.");
        }
    };

    const registrosPorDepartamento = departamento_poi.map(dept => {
        const registrosDept = registros.filter(r => r.departamento_id === dept.tipo_id);
        return {
            departamento_id: dept.tipo_id,
            nombre_departamento: dept.descripcion,
            actividad: actividadesPorDepartamento[dept.tipo_id] || 'N/A',
            linea_unidad: lineasUnidad[dept.tipo_id] || 'N/A',
            registros: registrosDept
        };
    });

    const getAlcancePorMes = (departamentoId, mesIndex) => {
        const mesPadded = String(mesIndex + 1).padStart(2, '0');
        const registro = registros.find(r =>
            r.departamento_id === departamentoId &&
            r.periodo?.endsWith(`-${mesPadded}`)
        );
        return registro ? ((parseFloat(registro.alcance) * 8.33) / 100).toFixed(2) + '%' : '0.00%';
    };

    const getTotalPorDepartamento = (departamentoId) => {
        const registrosDept = registros.filter(r => r.departamento_id === departamentoId);
        const total = registrosDept.reduce((sum, r) => sum + ((parseFloat(r.alcance || 0) * 8.33) / 100), 0);
        return total.toFixed(2) + '%';
    };

    const getTotalPorMes = (mesIndex) => {
        const mesPadded = String(mesIndex + 1).padStart(2, '0');
        const registrosMes = registros.filter(r => r.periodo?.endsWith(`-${mesPadded}`));
        const total = registrosMes.reduce((sum, r) => sum + ((parseFloat(r.alcance || 0) * 8.33) / 100), 0);
        return total.toFixed(2) + '%';
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="caja-flujo-container">
            <div className="flex items-center justify-between mb-4">
                <h1>CONTROL POI 2026</h1>
            </div>

            {canEdit && (
                <div className="nuevo-movimiento-section">
                    <ButtonInsert onClick={handleNuevoRegistro} />
                </div>
            )}

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th colSpan="2">Gerencias</th>
                            <th colSpan="2" rowSpan="1" className="text-center">Actividades estrategicas de corto plazo</th>
                            {meses.map((mes, index) => (
                                <th key={index} rowSpan="2">{mes}</th>
                            ))}
                            <th rowSpan="2">Total</th>
                            {canEdit && <th rowSpan="2">Acciones</th>}
                        </tr>
                        <tr>
                            <th colSpan="2">Lineas/unidad</th>
                            <th>Departamento</th>
                            <th>Actividad</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registrosPorDepartamento.length > 0 ? (
                            <>
                                {registrosPorDepartamento.map((item, index) => {
                                    const prevItem = registrosPorDepartamento[index - 1];
                                    const shouldRenderLineaUnidad = !prevItem || prevItem.linea_unidad !== item.linea_unidad;
                                    const sameLineaUnidadCount = registrosPorDepartamento.filter(
                                        r => r.linea_unidad === item.linea_unidad
                                    ).length;

                                    return (
                                        <tr key={item.departamento_id || index}>
                                            {index === 0 && (
                                                <td rowSpan={registrosPorDepartamento.length} style={{ verticalAlign: 'middle', textAlign: 'center' }}>GERENCIA GENERAL</td>
                                            )}
                                            {shouldRenderLineaUnidad && (
                                                <td rowSpan={sameLineaUnidadCount}>{item.linea_unidad}</td>
                                            )}
                                            <td>{item.nombre_departamento}</td>
                                            <td>{item.actividad}</td>
                                            {meses.map((mes, mesIndex) => {
                                                const mesPadded = String(mesIndex + 1).padStart(2, '0');
                                                const registro = registros.find(r =>
                                                    r.departamento_id === item.departamento_id &&
                                                    r.periodo?.endsWith(`-${mesPadded}`)
                                                );
                                                const alcance = registro ? ((parseFloat(registro.alcance) * 8.33) / 100).toFixed(2) + '%' : '0.00%';
                                                const tieneDocumento = registro?.tiene_documento_poi === '1' ||
                                                    registro?.tiene_documento_poi === 1 ||
                                                    registro?.tiene_archivo_documento_poi === '1' ||
                                                    registro?.tiene_archivo_documento_poi === 1 ||
                                                    registro?.tiene_documento === '1' ||
                                                    registro?.tiene_documento === 1;

                                                return (
                                                    <td key={mesIndex}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                                                            <span>{alcance}</span>
                                                            {registro && (
                                                                tieneDocumento ? (
                                                                    <button
                                                                        onClick={() => handleVerDocumentoPoi(registro.control_poi_id)}
                                                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#3cb167' }}
                                                                        title="Ver documento"
                                                                    >
                                                                        <FileMinus size={20} />
                                                                    </button>
                                                                ) : (
                                                                    <FileMinus size={20} style={{ color: '#f87171', opacity: 0.5 }} title="Sin documento" />
                                                                )
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                            <td>{getTotalPorDepartamento(item.departamento_id)}</td>
                                            {canEdit && (
                                                <td className="text-center" style={{ verticalAlign: 'middle' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                        {isDeptAllowed(item.departamento_id) && (
                                                            <ButtonUpdate
                                                                onClick={() => handleEditarRegistro(item.departamento_id)}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0' }}>
                                    <td colSpan={4}>TOTAL</td>
                                    {meses.map((mes, mesIndex) => (
                                        <td key={mesIndex}>{getTotalPorMes(mesIndex)}</td>
                                    ))}
                                    <td>FIN</td>
                                    {canEdit && <td></td>}
                                </tr>
                            </>
                        ) : (
                            <tr>
                                <td colSpan={18} className="text-center text-gray-500 py-4">
                                    No hay datos disponibles.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </Table>
            </div>

            <ModalControlPoi
                onSave={handleSaveRegistro}
                onClose={handleCloseModal}
                isOpen={isModalOpen}
                registro={selectedRegistro}
                registros={registros}
                departamentos={departamento_poi}
                usuarioId={state.user?.usuario_id}
                perfilId={state.user?.perfil_id}
            />
        </div >
    );
}

export default ControlPoi;